import React, { memo } from 'react'
import styled from 'styled-components'
import { Link } from 'react-router-dom'

import Text from '../../ui/Text'
import { ChargePointStatusStatistics } from '../../common/redux/api/web-api'
import SignalIcon from '../icons/signal-icon/signal-icon'
import SignalOffIcon from '../icons/signal-off-icon/signal-off-icon'
import { theme } from '../theme'
import BoltIcon from '../icons/bolt-icon/bolt-icon'

export type ChargePointStatusDashboardWidgetProps = {
    chargePointStatusStats?: ChargePointStatusStatistics
    style?: React.CSSProperties
    className?: string
}

const SLink = styled.a`
    & {
        color: ${({ theme }) => theme.font.darkestColor};
        text-decoration: underline;
        text-underline-offset: 5px;
        font-size: 1.25rem;
        font-weight: 400;
        line-height: 30px;
    }

    &:hover {
        color: ${({ theme }) => theme.font.successColor};
        cursor: pointer;
    }

    &:active {
        color: ${({ theme }) => theme.font.midDarkColor};
    }
`

export const ChargePointStatusDashboardWidget = memo(function ChargePointStatusDashboardWidget({
    style,
    chargePointStatusStats,
    className,
}: ChargePointStatusDashboardWidgetProps): React.ReactElement {
    function chargePointsStatusHref(status: 'Online' | 'Offline' | 'Charging'): string {
        return `/chargepoints?status=${status}`
    }

    return (
        <div style={style} className={className}>
            <div className="widget widget-success">
                <div className="widget-header">
                    <SignalIcon size={20} fill={theme.brandColors.CA_Green} />
                    <Text code="lblOnline"></Text>
                </div>
                <div className="dashboard-widget">
                    {chargePointStatusStats && chargePointStatusStats?.onlineCount !== 0 ? (
                        <SLink as={Link} to={chargePointsStatusHref('Online')} data-testid="online-count">
                            {chargePointStatusStats?.onlineCount || '---'}
                        </SLink>
                    ) : (
                        <div>{chargePointStatusStats?.onlineCount || '---'}</div>
                    )}
                </div>
            </div>

            <div className="widget widget-danger">
                <div className="widget-header">
                    <SignalOffIcon size={20} fill={theme.brandColors.CA_Coral} />
                    <Text code="lblOffline"></Text>
                </div>
                <div className="dashboard-widget">
                    {chargePointStatusStats && chargePointStatusStats?.offlineCount !== 0 ? (
                        <SLink as={Link} to={chargePointsStatusHref('Offline')} data-testid="offline-count">
                            {chargePointStatusStats?.offlineCount || '---'}
                        </SLink>
                    ) : (
                        <div>{chargePointStatusStats?.offlineCount || '---'}</div>
                    )}
                </div>
            </div>

            <div className="widget widget-info">
                <div className="widget-header">
                    <BoltIcon size={20} fill={theme.brandColors.CA_Blue} />
                    <Text code="lblChargingSockets"></Text>
                </div>
                <div className="dashboard-widget">
                    {chargePointStatusStats && chargePointStatusStats?.chargingCount !== 0 ? (
                        <SLink as={Link} to={chargePointsStatusHref('Charging')} data-testid="charging-count">
                            {chargePointStatusStats?.chargingCount || '---'}
                        </SLink>
                    ) : (
                        <div>{chargePointStatusStats?.chargingCount || '---'}</div>
                    )}
                </div>
            </div>
        </div>
    )
})
